import { inject, Injectable, InjectionToken, Signal, signal } from '@angular/core';
import { Router } from '@angular/router';

/**
 * What happens when the user leaves the cart to complete their purchase.
 *
 * The cart UI only knows that there is a "checkout" action; what it does is decided by
 * whichever handler the application provides. The default ({@link RouterCartCheckoutHandler})
 * navigates to the checkout form. The punchout app replaces it with a handler that submits
 * the cart back to the buyer's procurement system instead, so the checkout form is never shown
 * to a punchout user.
 */
export interface CartCheckoutHandler {
  /** Label for the cart's checkout button. */
  readonly label: Signal<string>;
  /** True while a checkout is in progress; the button is disabled meanwhile. */
  readonly busy: Signal<boolean>;
  checkout(): Promise<void>;
}

@Injectable({ providedIn: 'root' })
export class RouterCartCheckoutHandler implements CartCheckoutHandler {
  private readonly _router = inject(Router);

  public readonly label = signal('Proceed to Checkout').asReadonly();
  public readonly busy = signal(false).asReadonly();

  public async checkout(): Promise<void> {
    await this._router.navigate(['/checkout']);
  }
}

export const CART_CHECKOUT_HANDLER = new InjectionToken<CartCheckoutHandler>('CART_CHECKOUT_HANDLER', {
  providedIn: 'root',
  factory: () => inject(RouterCartCheckoutHandler),
});
